import { useSubscriptionOfferAccept } from "@/api";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "@tanstack/react-router";
import { FormEvent, useState } from "react";

export function useHandleSubscriptionOfferAccept({
  subscriptionOfferId
}: {
  subscriptionOfferId: string;
}) {
  const [loadingResolveAccept, setLoadingResolveAccept] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();
  const [subscriptionOfferAccept] = useSubscriptionOfferAccept();

  function errorToast() {
    toast({
      title: "Unable to accept subscription offer",
      description:
        "An error occurred while accepting the subscription offer. Please try again or report the issue."
    });
  }

  function handleAccept(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setLoadingResolveAccept(true);

    const formData = new FormData(e.currentTarget);
    const organizationId =
      (formData.get("organization") as string) || undefined;

    if (!organizationId) return;

    subscriptionOfferAccept({
      variables: { organizationId, subscriptionOfferId }
    })
      .then((res) => {
        if (res.errors) {
          console.error("Subscription offer accept errors:", res.errors);
          errorToast();
          return;
        }

        switch (res.data?.subscriptionOfferAccept.__typename) {
          case "AppSubscription": {
            toast({
              title: "Subscription offer accepted",
              description: `You can now use the app ${res.data.subscriptionOfferAccept.app.displayName}.`
            });
            navigate({
              to: "/organization/$organizationSlug/gallery",
              params: {
                organizationSlug:
                  res.data.subscriptionOfferAccept.inboundOrganization.slug
              },
              search: {
                filterName: res.data.subscriptionOfferAccept.app.displayName
              }
            });
            break;
          }
          case "SubscriptionOfferCheckoutSession": {
            window.location.replace(res.data.subscriptionOfferAccept.portalURL);
            break;
          }
          case "OrganizationMissingPaymentMethod": {
            toast({
              title: "Missing payment method",
              description:
                "Please add a payment method to your organization to accept the subscription offer."
            });
            break;
          }
          case "SubscriptionOfferInvalidStatus": {
            toast({
              title: "Invalid subscription offer",
              variant: "destructive",
              description:
                "The subscription offer is no longer valid. It has previosly been accepted, rejected, withdrawn or expired."
            });
            break;
          }
          case "SubscriptionOfferNotFound": {
            toast({
              title: "Subscription offer not found",
              variant: "destructive",
              description:
                "The subscription offer does not exist. Please contact support."
            });
            break;
          }
          default:
            errorToast();
        }
      })
      .catch((error) => {
        console.error("Subscription offer accept error:", error);
        errorToast();
      })
      .finally(() => setLoadingResolveAccept(false));
  }

  return { handleAccept, loadingResolveAccept };
}
